import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { invalidatePageCache } from './usePageCache';
import { useActiveSyncJobs, type ActiveSyncJobSummary } from './useActiveSyncJobs';
import { isTerminalSyncStage } from '../types/mailbox';

function readMaterializedCount(metrics: Record<string, unknown> | undefined): number {
    const value = metrics?.recent_workitems_materialized;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function shouldRefreshForJob(job: ActiveSyncJobSummary): boolean {
    if (!job.syncJobId || !job.currentStage || isTerminalSyncStage(job.currentStage)) {
        return false;
    }
    const metrics = job.metrics;
    if (metrics?.recent_first_unsupported_reason) {
        return false;
    }
    if (!metrics?.recent_slice_complete) {
        return false;
    }
    const count = readMaterializedCount(metrics);
    if (count <= 0) {
        return false;
    }
    return true;
}

/**
 * Optional one-shot Today/Workbench cache refresh when recent-slice metrics materialize (Story 9.5).
 * Subscribes to useActiveSyncJobs mailbox poll results — no duplicate /mailboxes timer.
 */
export function useRecentSliceRefresh(): void {
    const { session } = useAuth();
    const userId = session?.user?.id ?? null;
    const { activeJobs, loaded } = useActiveSyncJobs();
    const refreshedJobsRef = useRef<Set<string>>(new Set());
    const lastMaterializedRef = useRef<Record<string, number>>({});
    const prevActiveJobIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (userId == null) {
            refreshedJobsRef.current.clear();
            lastMaterializedRef.current = {};
            prevActiveJobIdsRef.current.clear();
            return;
        }
        if (!loaded) {
            return;
        }

        const currentActiveIds = new Set<string>();

        for (const job of activeJobs) {
            const jobId = job.syncJobId;
            if (!jobId) {
                continue;
            }
            currentActiveIds.add(jobId);

            if (!shouldRefreshForJob(job)) {
                continue;
            }

            const count = readMaterializedCount(job.metrics);
            const previous = lastMaterializedRef.current[jobId] ?? 0;
            lastMaterializedRef.current[jobId] = count;

            if (count > previous && count > 0 && !refreshedJobsRef.current.has(jobId)) {
                refreshedJobsRef.current.add(jobId);
                invalidatePageCache('today');
            }
        }

        // Fast-completing syncs: job left active set after materializing but before refresh fired.
        for (const prevId of Array.from(prevActiveJobIdsRef.current)) {
            if (currentActiveIds.has(prevId) || refreshedJobsRef.current.has(prevId)) {
                continue;
            }
            const lastCount = lastMaterializedRef.current[prevId] ?? 0;
            if (lastCount > 0) {
                refreshedJobsRef.current.add(prevId);
                invalidatePageCache('today');
            }
        }

        prevActiveJobIdsRef.current = currentActiveIds;

        // Prune tracking for completed jobs (unbounded Set guard — Story 9.5 P14).
        if (refreshedJobsRef.current.size > 200) {
            const keep = new Set(currentActiveIds);
            refreshedJobsRef.current = new Set(
                Array.from(refreshedJobsRef.current).filter((id) => keep.has(id)),
            );
        }
    }, [userId, loaded, activeJobs]);
}
